import type {VehicleCatalogMeta, VehicleMake, VehicleModel} from "../types";

function isActiveCatalogStatus(status: unknown): boolean {
  if (status === undefined || status === null) return true;
  if (typeof status === "number") return status === 1;
  const s = String(status).toLowerCase();
  return s === "active" || s === "1";
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function pickRows<T extends {name?: unknown; status?: unknown}>(
  raw: unknown,
  key: "vehicle_makes" | "vehicle_models",
  mapRow: (row: Record<string, unknown>) => T | null,
): T[] {
  const axiosData = asRecord(raw);
  const inner = asRecord(axiosData?.data) ?? axiosData;
  const nested = asRecord(inner?.data) ?? inner;
  const list = nested?.[key] ?? inner?.[key];
  if (!Array.isArray(list)) return [];
  const out: T[] = [];
  for (const item of list) {
    const row = asRecord(item);
    if (!row) continue;
    const mapped = mapRow(row);
    if (mapped && isActiveCatalogStatus(mapped.status)) out.push(mapped);
  }
  return out;
}

function num(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function normalizeVehicleMakesResponse(raw: unknown): VehicleMake[] {
  return pickRows<VehicleMake>(raw, "vehicle_makes", (row) => {
    const id = num(row.vehicle_make_id);
    const name = row.name;
    if (id == null || typeof name !== "string") return null;
    return {
      vehicle_make_id: id,
      name,
      status: row.status as string | number | undefined,
    };
  });
}

export function normalizeVehicleModelsResponse(raw: unknown): VehicleModel[] {
  return pickRows<VehicleModel>(raw, "vehicle_models", (row) => {
    const mid = num(row.vehicle_model_id);
    const mkid = num(row.vehicle_make_id);
    const name = row.name;
    if (mid == null || mkid == null || typeof name !== "string") return null;
    return {
      vehicle_model_id: mid,
      vehicle_make_id: mkid,
      name,
      status: row.status as string | number | undefined,
    };
  });
}

export function normalizeVehicleCatalogMeta(raw: unknown): VehicleCatalogMeta | null {
  const axiosData = asRecord(raw);
  const inner = asRecord(axiosData?.data) ?? axiosData;
  const meta = asRecord(inner?.meta);
  if (!meta) return null;
  const total = num(meta.total);
  const limit = num(meta.limit);
  const offset = num(meta.offset);
  const currentPage = num(meta.current_page);
  const lastPage = num(meta.last_page);
  if (total == null || limit == null || offset == null) return null;
  return {
    total,
    limit,
    offset,
    current_page: currentPage ?? Math.floor(offset / limit) + 1,
    last_page: lastPage ?? Math.max(1, Math.ceil(total / limit)),
  };
}
